import { createContext, ReactNode, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import { useUserContext } from "../user-context/UserContext";
import settings from "../../app-settings.json";
import signalRHubConfiguration, { SignalRHubEnum } from "../../hooks/use-signalr/SignalRHubInfo";
import { useSignalR } from "../../hooks/use-signalr/UseSignalR";
import { useOfflineDetectContext } from "../offline-detect-context/UseOfflineDetectContext";
import { ActionTypeEnum, IExportNotificationMsg, StatusEnum } from "../../common/IExportNotificationMsg";
import FileDownloader, { FileDownloaderType } from "../../components/file-downloader/FileDownloader";
import Enumerable from "linq";
import _, { omit } from "lodash";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useInterval } from "../../hooks/use-interval/UseInterval";


export const STATUS_NOTIFICATION_TIMEOUT = 120000;    // 2 minutes in milliseconds

export type ProgressContextProviderProps = { 
    children: ReactNode; 
};

export type ProgressStateItem = {

    uid: string;
    objectID: number;
    objectType: eLynxObjectTypeEnum;
    title: string;
    updatedOn?: Date | undefined;
    notificationMsg: IExportNotificationMsg //@@ here we can union types
    import?: boolean;
};

export type ProgressState = {
    items: ProgressStateItem[];
};

export enum ProgressActionType {
    ActionStarted = 'PROGRESS_ACTION_STARTED',
    StateItemsChanged = 'STATE_ITEM_CHANGED',
    ActionCanceled = 'PROGRESS_ACTION_CANCELED'
};


export type ProgressAction = | {
    type: ProgressActionType.ActionStarted;
    payload: ProgressStateItem;
} | {
    type: ProgressActionType.StateItemsChanged;
    payload: ProgressStateItem[];
} | {
    type: ProgressActionType.ActionCanceled;
    payload: string
};


function progressReducer(state: ProgressState, action: ProgressAction): ProgressState {

    switch (action.type) {
        case ProgressActionType.ActionStarted:

            return {...state,
                items: [...state.items, {
                    ...action.payload,
                    updatedOn: new Date(),
                    import: action.payload.import
                }]
            }

        case ProgressActionType.ActionCanceled:
            return {...state,
                items: [...state.items.filter(o => o.uid !== action.payload)
            ]}

        case ProgressActionType.StateItemsChanged:
                                                      
            return {...state,
                items: action.payload,
                
            }


        default: {
            return state;
        }
    }
}

const ProgressContext = createContext<[ProgressState, 
    (value: ProgressStateItem) => void,     // action started
    (uid: string) => void                   // action canceled
] | undefined>(undefined);

export function ProgressContextProvider({ children }: ProgressContextProviderProps) {

    const [progressState, dispatchProgressState] = useReducer(progressReducer, {
        items: []
    });

    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();

    const fileDownloaderRef = useRef<FileDownloaderType>(null);

    const actionStarted = useCallback((value: ProgressStateItem) => {

        dispatchProgressState({
            type: ProgressActionType.ActionStarted,
            payload: value
        })
    }, [
        dispatchProgressState
    ]);

    const actionCanceled = useCallback((value: string) => {

        dispatchProgressState({
            type: ProgressActionType.ActionCanceled,
            payload: value
        })
    }, [
        dispatchProgressState
    ]);


    const onCheckStaleStatus = useCallback(() => {

            //console.log('check stale status');

            // if signalR notifications do not occur within 2 minutes of the command start, then progress failed
            const expireDate: Date = new Date();
            expireDate.setMinutes(expireDate.getMinutes() - 5);

            const expiredItems = [...progressState.items].filter(o => o.notificationMsg.status !== StatusEnum.Completed && 
                o.updatedOn && 
                o.updatedOn.valueOf() < expireDate.valueOf());

            const newItems = [...expiredItems];

            expiredItems.forEach((o, index) => {

                const newItem: ProgressStateItem = {...o,
                    notificationMsg: {...o.notificationMsg,
                        status: StatusEnum.Failed,
                        statusMessage: t('progress.exportFailed')
                    }
                };

                newItems[index] = newItem;
            });

            if (newItems.length) {
                dispatchProgressState({
                    type: ProgressActionType.StateItemsChanged,
                    payload: newItems
                });
            }

    },[
        progressState.items,
        dispatchProgressState,
        t
    ]);


    useInterval(onCheckStaleStatus, 
                !progressState.items.length || offlineState.offline ? 
                    undefined : 
                    STATUS_NOTIFICATION_TIMEOUT
                );


    const onCallback = useCallback((value: IExportNotificationMsg) => {

        // workaround for bad % complete from backend.  Summary listing can report 150, 200 percent complete
        if (value.percentComplete > 100 && value.status !== StatusEnum.Completed) {
            value.percentComplete = 80;
        }

        console.log(`progress indicator: ${value.notificationID} ${value.status} pct: ${value.percentComplete} msg: ${value.statusMessage} add: ${value.additionalInfo}`);

        const ndx = progressState.items.findIndex(o => o.uid === value.notificationID);

        if (ndx !== -1) {
           
            const newItems = [...progressState.items];
            const newItem: ProgressStateItem = {...newItems[ndx],
                updatedOn: new Date()
            }

            if (!_.isEqual(omit(newItem, 'updatedOn'), omit(value, 'updatedOn'))) {

                newItem.notificationMsg = value;
                newItems[ndx] = newItem;

                dispatchProgressState({
                    type: ProgressActionType.StateItemsChanged,
                    payload: newItems
                });
            }

            if (value.action === ActionTypeEnum.Download && 
                value.status === StatusEnum.Completed && 
                value.blobURI && 
                !value.import) {
                fileDownloaderRef.current?.download(value.blobURI, 'file1');
            }
        }

    }, [
        progressState.items,
        dispatchProgressState
    ]);


    const hubConfiguration = signalRHubConfiguration.find(o => o.id === SignalRHubEnum.Export);
    useSignalR({
        url: `${settings.apiBaseUrl}${hubConfiguration?.path}`,
        customerID: userState.customer?.id,
        userID: userState.user?.id,
        methodName: hubConfiguration?.methodName as string,
        onCallback: onCallback,
            offline: offlineState.offline
        });    
        
    useEffect(() => {
        const newItems = [...progressState.items];

        const completed = Enumerable.from(progressState.items)
                            .where(o => o.notificationMsg.status === StatusEnum.Completed || 
                                o.notificationMsg.status === StatusEnum.Failed)
                            .toArray();

        // remove any completed items
        if (completed.length) {

            let timerID = window.setTimeout(() => {
                completed.forEach((o, index) => {
    
                    newItems.splice(index, 1);
        
                });
    
                dispatchProgressState({
                    type: ProgressActionType.StateItemsChanged,
                    payload: newItems
                });

            }, 5000);

            return () => {
                window.clearTimeout(timerID);
            }
        }
    }, [
        progressState.items,
        dispatchProgressState
    ]);

    return (
        <ProgressContext.Provider value={[ 
                progressState,
                actionStarted,
                actionCanceled
            ]}>
                <>
                {children}
                <FileDownloader ref={fileDownloaderRef} />        
                </>
            
        </ProgressContext.Provider>
    );
}

export function useProgressContext() {
    const context = useContext<[ProgressState,
         (value: ProgressStateItem) => void,     // action started
         (uid: string) => void                   // action canceled
        ] | undefined>(ProgressContext);
    if (!context) {
        throw new Error("useProgressContext must be used within an ProgressContextProvider");
    }
    return context;
}
